"use client";

import { useState, useEffect } from "react";
import { Search, Filter, Trash2, Reply, Eye, CheckCircle } from "lucide-react";
import Card from "../components/Card";
import Table from "../components/Table";
import Button from "../components/Button";
import Badge from "../components/Badge";
import Pagination from "../components/Pagination";
import Modal from "../components/Modal";

// Generate mock contact submissions
const generateMockContacts = () => {
  const subjects = [
    "General Inquiry",
    "Product Support",
    "Billing Question",
    "Partnership Opportunity",
    "Feature Request",
    "Bug Report",
    "Feedback",
    "Job Application",
    "Press Inquiry",
    "Other",
  ];

  return Array(40)
    .fill()
    .map((_, i) => ({
      id: i + 1,
      name: `Contact ${i + 1}`,
      email: `contact${i + 1}@example.com`,
      subject: subjects[i % subjects.length],
      message: `This is a sample message from Contact ${
        i + 1
      }. It contains the details of their inquiry or feedback. This is just placeholder text to simulate a real message that might be sent through a contact form.`,
      status: i % 3 === 0 ? "read" : i % 3 === 1 ? "unread" : "replied",
      created_at: new Date(
        Date.now() - Math.floor(Math.random() * 10000000000)
      ).toISOString(),
      replied_at:
        i % 3 === 2
          ? new Date(
              Date.now() - Math.floor(Math.random() * 5000000000)
            ).toISOString()
          : null,
    }));
};

const Contact = () => {
  const [contacts, setContacts] = useState([]);
  const [filteredContacts, setFilteredContacts] = useState([]);
  const [displayedContacts, setDisplayedContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isReplyModalOpen, setIsReplyModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({
    status: "",
  });
  const [replyText, setReplyText] = useState("");

  // Load mock data
  useEffect(() => {
    const timer = setTimeout(() => {
      const mockContacts = generateMockContacts();
      setContacts(mockContacts);
      setFilteredContacts(mockContacts);
      applyPagination(mockContacts, 1);
      setLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  // Apply filters and search
  useEffect(() => {
    let result = [...contacts];

    // Apply search
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      result = result.filter(
        (contact) =>
          contact.name.toLowerCase().includes(search) ||
          contact.email.toLowerCase().includes(search) ||
          contact.subject.toLowerCase().includes(search) ||
          contact.message.toLowerCase().includes(search)
      );
    }

    // Apply status filter
    if (filters.status) {
      result = result.filter((contact) => contact.status === filters.status);
    }

    setFilteredContacts(result);
    setTotalPages(Math.ceil(result.length / 10));
    applyPagination(result, 1);
  }, [contacts, searchTerm, filters]);

  // Apply pagination
  const applyPagination = (data, page) => {
    const pageSize = 10;
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    setDisplayedContacts(data.slice(startIndex, endIndex));
    setCurrentPage(page);
  };

  const goToPage = (page) => {
    applyPagination(filteredContacts, page);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    // Search is already applied via useEffect
  };

  const handleFilterChange = (newFilters) => {
    setFilters({ ...filters, ...newFilters });
    setShowFilters(false);
  };

  const handleViewContact = (contact) => {
    // Mark as read if unread
    if (contact.status === "unread") {
      const updatedContacts = contacts.map((c) => {
        if (c.id === contact.id) {
          return { ...c, status: "read" };
        }
        return c;
      });
      setContacts(updatedContacts);
    }

    setSelectedContact(contact);
    setIsViewModalOpen(true);
  };

  const handleReplyContact = (contact) => {
    setSelectedContact(contact);
    setReplyText("");
    setIsReplyModalOpen(true);
  };

  const handleDeleteContact = (contact) => {
    setSelectedContact(contact);
    setIsDeleteModalOpen(true);
  };

  const handleSendReply = () => {
    if (!replyText.trim()) return;

    // Update contact status to replied
    const updatedContacts = contacts.map((c) => {
      if (c.id === selectedContact.id) {
        return {
          ...c,
          status: "replied",
          replied_at: new Date().toISOString(),
        };
      }
      return c;
    });
    setContacts(updatedContacts);

    // Show toast message (simplified)
    console.log(`Reply sent to ${selectedContact.email}`);

    // Close the modal
    setIsReplyModalOpen(false);
  };

  const handleConfirmDelete = () => {
    // Delete contact from the mock data
    const updatedContacts = contacts.filter((c) => c.id !== selectedContact.id);
    setContacts(updatedContacts);

    // Show toast message (simplified)
    console.log(
      `Contact message from ${selectedContact.name} has been deleted`
    );

    // Close the modal
    setIsDeleteModalOpen(false);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "read":
        return <Badge variant="gray">Read</Badge>;
      case "unread":
        return <Badge variant="blue">Unread</Badge>;
      case "replied":
        return <Badge variant="green">Replied</Badge>;
      default:
        return <Badge variant="gray">{status}</Badge>;
    }
  };

  const columns = [
    {
      header: "Contact",
      accessor: "name",
      cell: (row) => (
        <div>
          <div className="font-medium text-gray-900">{row.name}</div>
          <div className="text-sm text-gray-500">{row.email}</div>
        </div>
      ),
    },
    {
      header: "Subject",
      accessor: "subject",
      cell: (row) => <span className="text-gray-900">{row.subject}</span>,
    },
    {
      header: "Status",
      accessor: "status",
      cell: (row) => getStatusBadge(row.status),
    },
    {
      header: "Date",
      accessor: "created_at",
      cell: (row) => (
        <span className="text-gray-500">
          {new Date(row.created_at).toLocaleDateString()}
        </span>
      ),
    },
    {
      header: "Actions",
      accessor: "actions",
      cell: (row) => (
        <div className="flex space-x-2">
          <Button
            variant="outline"
            size="sm"
            icon={Eye}
            onClick={(e) => {
              e.stopPropagation();
              handleViewContact(row);
            }}
          />
          <Button
            variant="outline"
            size="sm"
            icon={Reply}
            onClick={(e) => {
              e.stopPropagation();
              handleReplyContact(row);
            }}
          />
          <Button
            variant="outline"
            size="sm"
            icon={Trash2}
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteContact(row);
            }}
          />
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Contact Messages</h1>
        <p className="mt-1 text-sm text-gray-500">
          Manage and respond to contact form submissions
        </p>
      </div>

      <Card>
        <div className="flex flex-col sm:flex-row justify-between mb-4">
          <form onSubmit={handleSearch} className="flex mb-4 sm:mb-0">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Search messages..."
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <Button type="submit" variant="outline" className="ml-2">
              Search
            </Button>
            <Button
              type="button"
              variant="outline"
              icon={Filter}
              className="ml-2"
              onClick={() => setShowFilters(!showFilters)}
            >
              Filter
            </Button>
          </form>
          <div className="flex items-center">
            <span className="text-sm text-gray-500 mr-2">
              {contacts.filter((c) => c.status === "unread").length} unread
              messages
            </span>
          </div>
        </div>

        {showFilters && (
          <div className="mb-4 p-4 border border-gray-200 rounded-md bg-gray-50">
            <h3 className="text-sm font-medium text-gray-700 mb-2">Filters</h3>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                onChange={(e) =>
                  handleFilterChange({ status: e.target.value || "" })
                }
                value={filters.status}
              >
                <option value="">All Status</option>
                <option value="unread">Unread</option>
                <option value="read">Read</option>
                <option value="replied">Replied</option>
              </select>
            </div>
          </div>
        )}

        <Table
          columns={columns}
          data={displayedContacts}
          isLoading={loading}
          onRowClick={handleViewContact}
          emptyState="No contact messages found. Try adjusting your search or filters."
        />

        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={goToPage}
        />
      </Card>

      {/* View Contact Modal */}
      <Modal
        isOpen={isViewModalOpen}
        onClose={() => setIsViewModalOpen(false)}
        title="View Message"
        size="lg"
        footer={
          <div className="flex justify-between w-full">
            <Button
              variant="danger"
              icon={Trash2}
              onClick={() => {
                setIsViewModalOpen(false);
                handleDeleteContact(selectedContact);
              }}
            >
              Delete
            </Button>
            <div className="space-x-2">
              <Button
                variant="outline"
                onClick={() => setIsViewModalOpen(false)}
              >
                Close
              </Button>
              <Button
                icon={Reply}
                onClick={() => {
                  setIsViewModalOpen(false);
                  handleReplyContact(selectedContact);
                }}
              >
                Reply
              </Button>
            </div>
          </div>
        }
      >
        {selectedContact && (
          <div className="space-y-4">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="text-lg font-medium text-gray-900">
                  {selectedContact.subject}
                </h3>
                <div className="mt-1 flex items-center text-sm text-gray-500">
                  <span>
                    From: {selectedContact.name} &lt;{selectedContact.email}&gt;
                  </span>
                </div>
                <div className="mt-1 flex items-center text-sm text-gray-500">
                  <span>
                    Received:{" "}
                    {new Date(selectedContact.created_at).toLocaleString()}
                  </span>
                </div>
              </div>
              <div>{getStatusBadge(selectedContact.status)}</div>
            </div>
            <div className="border-t border-gray-200 pt-4">
              <p className="text-gray-700 whitespace-pre-line">
                {selectedContact.message}
              </p>
            </div>

            {selectedContact.status === "replied" && (
              <div className="border-t border-gray-200 pt-4">
                <div className="flex items-center mb-2">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                  <span className="text-sm font-medium text-gray-700">
                    Replied on{" "}
                    {new Date(selectedContact.replied_at).toLocaleString()}
                  </span>
                </div>
                <div className="bg-gray-50 p-3 rounded-md">
                  <p className="text-gray-700 text-sm italic">
                    Reply content would appear here in a real application.
                  </p>
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Reply Modal */}
      <Modal
        isOpen={isReplyModalOpen}
        onClose={() => setIsReplyModalOpen(false)}
        title="Reply to Message"
        size="lg"
        footer={
          <>
            <Button
              variant="outline"
              onClick={() => setIsReplyModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              className="ml-3"
              onClick={handleSendReply}
              disabled={!replyText.trim()}
            >
              Send Reply
            </Button>
          </>
        }
      >
        {selectedContact && (
          <div className="space-y-4">
            <div className="bg-gray-50 p-4 rounded-md">
              <div className="flex justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-700">
                    From: {selectedContact.name} &lt;{selectedContact.email}&gt;
                  </p>
                  <p className="text-sm font-medium text-gray-700">
                    Subject: {selectedContact.subject}
                  </p>
                </div>
                <div className="text-sm text-gray-500">
                  {new Date(selectedContact.created_at).toLocaleDateString()}
                </div>
              </div>
              <div className="mt-2 text-sm text-gray-700 border-t border-gray-200 pt-2">
                {selectedContact.message}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Your Reply
              </label>
              <textarea
                rows={8}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                placeholder="Type your reply here..."
              />
            </div>
          </div>
        )}
      </Modal>

      {/* Delete Contact Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Delete Message"
        size="sm"
        footer={
          <>
            <Button
              variant="outline"
              onClick={() => setIsDeleteModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              className="ml-3"
              onClick={handleConfirmDelete}
            >
              Delete
            </Button>
          </>
        }
      >
        {selectedContact && (
          <p className="text-sm text-gray-500">
            Are you sure you want to delete this message from{" "}
            {selectedContact.name}? This action cannot be undone.
          </p>
        )}
      </Modal>
    </div>
  );
};

export default Contact;
